/**
 * ADR-213 — personal offers on the web cart: `/cart?offer=<token>`.
 *
 * Desk sends a link; it lands on the home page with the cart drawer open. Each
 * offered product has its own discount (percent or fixed IQD per unit, up to
 * maxQuantity units); what the customer adds is at full price. This file holds
 * the stored token, the landing and the debounced quote (or the signed-out
 * estimate); eros-offer-rules.js is pure and eros-offer-render.js draws.
 * Transport: window.ErosCommerce (its credentials:'include' carries the session).
 */
(function (root) {
  'use strict';
  if (root.ErosOffer || !root.ErosOfferRules) return; /* the cart page and the drawer both load this file */

  var R = root.ErosOfferRules;
  var STORE_KEY = R.STORE_KEY;
  var isValidToken = R.isValidToken;
  var parseStored = R.parseStored;
  var quoteLines = R.quoteLines;
  var cartSignature = R.cartSignature;
  var lineViews = R.lineViews;
  var viewFromState = R.viewFromState;
  var variantsToAdd = R.variantsToAdd;
  var toMs = R.toMs;
  var estimateOffer = R.estimateOffer;
  var estimateItems = R.estimateItems;
  var QUOTE_DEBOUNCE_MS = 800;
  var SYNC_DEBOUNCE_MS = 120;
  var CACHE_MAX = 6;
  var FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

  var st = {
    token: null,
    expiresAt: null,
    storedState: null,
    lines: null /* preview lines for the signed-out estimate; null = never fetched */,
    linesTried: false,
    estimate: null,
    quote: null,
    failed: false,
    ended: false
  };
  var cache = [];
  var syncTimer = null;
  var quoteTimer = null;
  var seq = 0;

  function readStore() {
    try {
      var raw = root.localStorage.getItem(STORE_KEY);
      return raw ? parseStored(raw, Date.now()) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(v) {
    try {
      if (v) root.localStorage.setItem(STORE_KEY, JSON.stringify(v));
      else root.localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* private mode: the offer lives for this page only */
    }
  }

  function signedIn() {
    return !!(root.ErosSession && root.ErosSession.isSignedIn());
  }

  function snapshot() {
    var out = signedIn();
    var view = viewFromState(
      {
        token: st.token,
        expiresAt: st.expiresAt,
        storedState: st.storedState,
        signedIn: out,
        quote: st.quote,
        failed: st.failed,
        ended: st.ended,
        lines: st.lines,
        estimate: st.estimate
      },
      Date.now()
    );
    var source = out ? st.quote && st.quote.offer : st.estimate; /* ADR-213 §2.6: signed in = server quote only */
    return {
      view: view,
      lines: view && view.kind === 'applies' && source ? lineViews(source) : [],
      discountIqd: view && view.kind === 'applies' ? view.amountIqd : 0
    };
  }

  function notify() {
    document.dispatchEvent(new CustomEvent('eros:offer-state', { detail: snapshot() }));
  }

  function activeToken() {
    if (!st.token) return null;
    if (st.expiresAt !== null && st.expiresAt <= Date.now()) return null;
    return st.token;
  }

  function adopt(stored) {
    st.token = stored ? stored.token : null;
    st.expiresAt = stored ? stored.expiresAt : null;
    st.storedState = stored ? stored.state : null;
    st.lines = stored ? stored.lines : null;
    st.linesTried = false;
    st.estimate = null;
    st.quote = null;
    st.failed = false;
  }

  function clear() {
    adopt(null);
    st.ended = false;
    cache = [];
    writeStore(null);
    notify();
  }

  /** The countdown reached zero: say so, forget the token, re-price without it. */
  function expire() {
    if (!st.token) return;
    adopt(null);
    st.ended = true;
    cache = [];
    writeStore(null);
    notify();
    document.dispatchEvent(new CustomEvent('eros:offer-ended'));
  }

  function cartRoot() {
    var r = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return r.replace(/\/?$/, '/');
  }

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin' }).then(function (res) {
      return res.json();
    });
  }

  function whenReady(fn, tries) {
    if (root.ErosCommerce && root.ErosSession) return fn();
    if ((tries || 0) > 50) return undefined;
    return setTimeout(function () {
      whenReady(fn, (tries || 0) + 1);
    }, 100);
  }

  function remember(sig, quote) {
    cache = cache.filter(function (c) {
      return c.sig !== sig;
    });
    cache.push({ sig: sig, quote: quote });
    if (cache.length > CACHE_MAX) cache.shift();
  }

  function cached(sig) {
    for (var i = 0; i < cache.length; i++) if (cache[i].sig === sig) return cache[i].quote;
    return null;
  }

  function requestQuote(sig, cart, mySeq) {
    root.ErosCommerce.checkoutQuote((root.ErosPromoRules || R).quoteLines(cart.items), undefined, false, undefined, st.token) /* ADR-332: the gift property travels */
      .then(function (q) {
        if (mySeq !== seq || !st.token) return;
        if (R.isOtherCustomerQuote(q)) {
          clear(); /* ADR-218: another account's offer: the products stay at full price, nothing is said */
          return;
        }
        remember(sig, q);
        st.quote = q;
        document.dispatchEvent(new CustomEvent('eros:quote', { detail: q })); /* ADR-332: the promotions reuse this quote */
        var exp = q && q.offer ? toMs(q.offer.expiresAt) : null;
        if (exp !== null && exp !== st.expiresAt) {
          st.expiresAt = exp;
          writeStore({ token: st.token, expiresAt: exp, state: st.storedState, lines: st.lines });
        }
        notify();
      })
      .catch(function () {
        if (mySeq !== seq) return;
        st.quote = null;
        st.failed = true;
        notify(); /* the plain cart total stays; checkout asks the worker again */
      });
  }

  function fetchPreview(token) {
    return root.ErosCommerce.offerPreview(token).catch(function (err) {
      /* a refusal that still names the state (ok:false) is an answer, not a failure */
      if (err && err.details && typeof err.details.state === 'string') return err.details;
      throw err;
    });
  }

  /** A record stored before §2.6 has no lines: ask the preview once, keep the answer. */
  function ensureLines() {
    st.linesTried = true;
    return new Promise(function (resolve) {
      whenReady(function () {
        fetchPreview(st.token)
          .then(function (preview) {
            var rec = R.recordFromPreview(st.token, preview, Date.now(), FALLBACK_TTL_MS);
            if (!rec) return clear(); /* ADR-218: another account's offer */
            st.expiresAt = rec.expiresAt;
            st.storedState = rec.state;
            st.lines = rec.lines;
            writeStore(rec);
          })
          .catch(function () {}) /* offline: «sign in» as before; the next page load asks again */
          .then(resolve);
      });
    });
  }

  /** Signed out: price the stored offer lines against the cart's own prices (no network but /cart.js). */
  function estimateSignedOut(mySeq) {
    if (st.lines === null && !st.linesTried && R.isLiveState(st.storedState)) {
      notify();
      ensureLines().then(function () {
        if (mySeq === seq && st.token) estimateSignedOut(mySeq);
      });
      return;
    }
    if (!st.lines || !st.lines.length || !R.isLiveState(st.storedState)) {
      st.estimate = null;
      notify();
      return;
    }
    fetchCart()
      .then(function (cart) {
        if (mySeq !== seq) return;
        st.estimate = estimateOffer(st.lines, estimateItems(cart.items));
        notify();
      })
      .catch(function () {
        if (mySeq !== seq) return;
        st.failed = true; /* offline: the line falls back to «sign in» */
        notify();
      });
  }

  /** Read the cart, answer from the cache when the cart is the same, else quote (debounced). */
  function sync() {
    seq += 1;
    var mySeq = seq;
    st.failed = false;
    if (!activeToken()) {
      st.quote = null;
      notify();
      return;
    }
    if (!signedIn()) {
      st.quote = null;
      estimateSignedOut(mySeq);
      return;
    }
    st.estimate = null;
    fetchCart()
      .then(function (cart) {
        if (mySeq !== seq) return;
        if (!cart.items || !cart.items.length) {
          st.quote = null;
          notify();
          return;
        }
        var sig = cartSignature(cart.items, st.token);
        var hit = cached(sig);
        if (hit) {
          st.quote = hit;
          notify();
          return;
        }
        st.quote = null;
        notify(); /* «…» in the line until the answer lands */
        clearTimeout(quoteTimer);
        quoteTimer = setTimeout(function () {
          whenReady(function () {
            requestQuote(sig, cart, mySeq);
          });
        }, QUOTE_DEBOUNCE_MS);
      })
      .catch(function () {
        /* offline: keep the plain cart */
      });
  }

  function schedule() {
    clearTimeout(syncTimer);
    clearTimeout(quoteTimer);
    syncTimer = setTimeout(sync, SYNC_DEBOUNCE_MS);
  }

  /* One unit of each, at full price; the shared cart transport (eros-cart-add-link.js) removes gift cards. */
  function addVariants(ids) {
    if (!root.ErosCartAdd) return Promise.resolve(0);
    return root.ErosCartAdd.addLines(
      ids.map(function (id) {
        return { variantId: id, quantity: 1 };
      })
    );
  }

  /* The link lands on the page it opened (layout/theme.liquid sends /cart?offer= to the
     home page before it renders): drop the param, refresh the drawer, open it (owner 2026-10-07). */
  function stripOfferParam(params) {
    params.delete('offer');
    var rest = params.toString();
    history.replaceState(history.state, '', location.pathname + (rest ? '?' + rest : '') + location.hash);
    start(); /* the page did not reload: adopt the offer it just stored */
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    if (root.ErosOfferRender) root.ErosOfferRender.openDrawer();
  }

  /** `?offer=` landing: store, preview, add the offered products, strip the param. */
  function land(token, params) {
    if (!isValidToken(token)) {
      stripOfferParam(params);
      return;
    }
    var now = Date.now();
    writeStore({ token: token, expiresAt: now + FALLBACK_TTL_MS, state: null, lines: null });
    whenReady(function () {
      var done = function () {
        stripOfferParam(params);
      };
      fetchPreview(token)
        .then(function (preview) {
          writeStore(R.recordFromPreview(token, preview, now, FALLBACK_TTL_MS));
          return fetchCart().then(function (cart) {
            var have = (cart.items || []).map(function (it) {
              return it.variant_id;
            });
            return addVariants(variantsToAdd(preview, have));
          });
        })
        .catch(function () {}) /* the token stays stored; the cart asks the worker again */
        .then(done, done);
    });
  }

  function init() {
    var params = new URLSearchParams(location.search);
    if (params.has('offer')) land(params.get('offer'), params);
    else start();
  }

  /* Adopt the stored offer and price the cart on every change; a landing calls it once done. */
  function start() {
    adopt(readStore());
    document.addEventListener('cart:change', schedule);
    document.addEventListener('cart:refresh', schedule);
    whenReady(function () {
      if (root.ErosSession.onChange) {
        var first = true;
        root.ErosSession.onChange(function () {
          if (first) {
            first = false;
            return;
          }
          cache = [];
          schedule();
        });
      }
      schedule();
    });
  }

  root.ErosOffer = Object.assign({}, R, {
    activeToken: activeToken,
    clear: clear,
    expire: expire,
    snapshot: snapshot,
    refresh: schedule
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window !== 'undefined' ? window : globalThis);
